import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save, Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { MetaPage, PageVariableInput } from '../types/meta.types';
import {
    getPageVariables,
    bulkUpsertPageVariables,
    getMetaPages,
} from '@/services/api';
import PageVariablesCopyModal from './PageVariablesCopyModal';
import { useToast } from '@/hooks/use-toast';

interface PageVariablesPanelProps {
    page: MetaPage;
}

interface VariableRow {
    id?: number;
    key: string;
    value: string;
    dynamism: boolean;
}

export default function PageVariablesPanel({ page }: PageVariablesPanelProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [rows, setRows] = useState<VariableRow[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [copyModalOpen, setCopyModalOpen] = useState(false);

    const { data: pages = [] } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
    });

    const { isLoading, data: variablesData } = useQuery({
        queryKey: ['page-variables', page.id],
        queryFn: () => getPageVariables(page.id),
        enabled: !!page.id,
    });

    useEffect(() => {
        if (variablesData) {
            setRows(variablesData.map(v => ({ id: v.id, key: v.key, value: v.value, dynamism: v.dynamism })));
            setIsDirty(false);
        }
    }, [variablesData]);

    const saveMutation = useMutation({
        mutationFn: (variables: PageVariableInput[]) => bulkUpsertPageVariables(page.id, variables),
        onSuccess: (data) => {
            setRows(data.map(v => ({ id: v.id, key: v.key, value: v.value, dynamism: v.dynamism })));
            setIsDirty(false);
            queryClient.invalidateQueries({ queryKey: ['page-variables', page.id] });
            toast({ title: 'Variáveis salvas!', description: 'As variáveis foram atualizadas com sucesso.' });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const addRow = () => {
        setRows(prev => [...prev, { key: '', value: '', dynamism: false }]);
        setIsDirty(true);
    };

    const updateRow = (index: number, field: keyof VariableRow, value: string | boolean | null) => {
        setRows(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
        setIsDirty(true);
    };

    const removeRow = (index: number) => {
        setRows(prev => prev.filter((_, i) => i !== index));
        setIsDirty(true);
    };

    const handleSave = () => {
        const validRows = rows.filter(r => r.key.trim() && r.value.trim());
        saveMutation.mutate(validRows.map(r => ({ key: r.key.trim(), value: r.value.trim(), dynamism: r.dynamism })));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Table */}
            <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[35%]">
                                Chave
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Valor
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[120px]">
                                Dinamismo
                            </th>
                            <th className="px-4 py-3 w-[50px]" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm italic">
                                    Nenhuma variável cadastrada. Clique em "+ Adicionar" para criar.
                                </td>
                            </tr>
                        )}
                        {rows.map((row, index) => (
                            <tr key={index} className="hover:bg-gray-50/50 group">
                                <td className="px-3 py-2">
                                    <Input
                                        value={row.key}
                                        onChange={(e) => updateRow(index, 'key', e.target.value)}
                                        placeholder="UTM_CAMPAIGN"
                                        className="h-8 text-xs font-mono border-0 bg-transparent focus:bg-white focus:border-gray-200 rounded-lg"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <Input
                                        value={row.value}
                                        onChange={(e) => updateRow(index, 'value', e.target.value)}
                                        placeholder="Valor..."
                                        className="h-8 text-xs border-0 bg-transparent focus:bg-white focus:border-gray-200 rounded-lg"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <Select
                                        value={row.dynamism ? 'meta_ad_id' : 'none'}
                                        onValueChange={(value) => updateRow(index, 'dynamism', value === 'meta_ad_id')}
                                    >
                                        <SelectTrigger className="h-8 text-xs border-0 bg-transparent focus:bg-white focus:border-gray-200 rounded-lg">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Nenhum</SelectItem>
                                            <SelectItem value="meta_ad_id">Meta Ad ID</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button
                                        onClick={() => removeRow(index)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={addRow}
                        className="text-gray-600 hover:text-[#0f172a] rounded-xl"
                    >
                        <Plus className="w-4 h-4 mr-1.5" />
                        Adicionar variável
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCopyModalOpen(true)}
                        className="text-gray-600 hover:text-[#0f172a] rounded-xl border-gray-200"
                    >
                        <Copy className="w-4 h-4 mr-1.5" />
                        Copiar variáveis
                    </Button>
                </div>

                {isDirty && (
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saveMutation.isPending}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl"
                    >
                        {saveMutation.isPending ? (
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-1.5" />
                        )}
                        Salvar variáveis
                    </Button>
                )}
            </div>

            <PageVariablesCopyModal
                open={copyModalOpen}
                onOpenChange={setCopyModalOpen}
                currentPage={page}
                pages={pages}
                onSuccess={() => queryClient.invalidateQueries({ queryKey: ['page-variables', page.id] })}
            />
        </div>
    );
}
